/**
 * ProjectPageLayout
 *
 * Shared layout wrapper for all project pages providing:
 * - No breadcrumb navigation (removed in #132 with the overview v2 persistent header)
 * - Tab navigation (Overview, Plan, Tasks, Risks, Resources)
 * - Loading and error states
 * - Consistent spacing and structure
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Folder, LayoutDashboard, ListTodo, AlertTriangle, Users, LayoutGrid, Table2, BarChart3, Calendar, GitPullRequest, FileText, DollarSign, Activity, Shield } from 'lucide-react';
import { useWorkspaceStore } from '@/state/workspace.store';
import { getWorkspace } from '@/features/workspaces/api';
import { PROJECT_TAB_SWITCH } from '@/features/feature-switches/featureSwitchMap';
import { useFeatureOn } from '@/features/feature-switches/featureSwitchSession';
// useProjectPermissions moved to toolbar ... menu (ProjectTasksTab)
import {
  getProjectCapabilities,
  type ProjectCapabilities,
} from '../capabilities';
import { projectsApi, type ProjectDetail } from '../projects.api';
import { ProjectLifecycleControls } from '../components/ProjectLifecycleControls';
import { ProjectWorkToolbar } from '../components/ProjectWorkToolbar';
import { WorkSurfaceUiProvider } from './WorkSurfaceUiContext';
import { EmptyState } from '@/components/ui/feedback/EmptyState';
import { SaveAsTemplateModal } from '../components/SaveAsTemplateModal';
import { DuplicateProjectModal } from '../components/DuplicateProjectModal';
import { ProjectHeaderActionsMenu } from '../components/ProjectHeaderActionsMenu';
import { ProjectAddViewPopover } from '../components/ProjectAddViewPopover';
import {
  readVisibleTabIds,
  sortTabIdsByOrder,
  tabOrderIndex,
} from './projectVisibleTabs';
import { stripLegacyVisibleTabs } from './stripLegacyVisibleTabs';
import {
  normalizeColumnConfig,
  withVisibleTabs,
} from '@/features/templates/columnConfig.model';
// ProjectIdentityFrame removed — project name + description now in persistent header
import { api, unwrapApiData } from '@/lib/api';
import { useAuth } from '@/state/AuthContext';
import { isPlatformAdmin } from '@/utils/access';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import {
  normalizeProjectOverview,
  type ProjectOverview,
} from '../model/projectOverview';
import type { ProjectPlan } from '@/features/work-management/workTasks.api';
import {
  mapProjectPlanFromApi,
  projectHasActiveGateDefinitions,
} from '@/features/work-management/projectPlan.mappers';
import { isProjectReadOnly } from '../projectStateDisplay';

/**
 * Tab configuration for project pages
 *
 * HR3 (Template Center MVP source of truth): Only 4 tabs are visible in MVP project shell.
 * Other tab routes remain registered in App.tsx for direct URL access, but the visible
 * tab rail is limited to the approved MVP set: Overview, Activities, Board, Gantt, Calendar.
 *
 * Phase 2 will rename "Tasks" → "Activities" and build the real Activities surface.
 * For Phase 1, "Tasks" is the closest existing tab to "Activities".
 */
const PROJECT_TABS_ALL = [
  { id: 'overview', label: 'Overview', path: '', icon: LayoutDashboard },
  { id: 'plan', label: 'Plan', path: '/plan', icon: Folder },
  { id: 'tasks', label: 'Activities', path: '/tasks', icon: ListTodo },
  { id: 'board', label: 'Board', path: '/board', icon: LayoutGrid },
  { id: 'table', label: 'Table', path: '/table', icon: Table2 },
  { id: 'gantt', label: 'Gantt', path: '/gantt', icon: BarChart3 },
  { id: 'calendar', label: 'Calendar', path: '/calendar', icon: Calendar },
  { id: 'documents', label: 'Documents', path: '/documents', icon: FileText },
  { id: 'governance', label: 'Governance', path: '/governance', icon: Shield },
  { id: 'risks', label: 'Risks', path: '/risks', icon: AlertTriangle },
  { id: 'resources', label: 'Resources', path: '/resources', icon: Users },
  { id: 'change-requests', label: 'Change Requests', path: '/change-requests', icon: GitPullRequest },
  { id: 'budget', label: 'Budget', path: '/budget', icon: DollarSign },
  { id: 'kpis', label: 'KPIs', path: '/kpis', icon: Activity },
] as const;

type TabId = (typeof PROJECT_TABS_ALL)[number]['id'];
type ProjectTab = (typeof PROJECT_TABS_ALL)[number];

interface ProjectContextValue {
  project: ProjectDetail | null;
  /** Resolved methodology capabilities — fetched once per project load. */
  capabilities: ProjectCapabilities | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Resolved display name for the project's workspace (null if fetch failed). */
  workspaceDisplayName: string | null;
  /** Overview payload for identity frame + Overview tab (single fetch path). */
  overviewSnapshot: ProjectOverview | null;
  overviewLoading: boolean;
  refreshOverviewSnapshot: () => Promise<void>;
  /**
   * GET /work/projects/:id/plan — single shell load for OV-1.
   * Null when not loaded or request failed (cannot prove live gates).
   */
  projectPlan: ProjectPlan | null;
  /** True when plan has ≥1 phase with gate.definitionExists (earned Governed badge). */
  hasLiveGovernance: boolean;
  /**
   * Set when GET /plan failed. Distinguishes "checked, no gates" from "could not check".
   * Do not treat a missing badge alone as "ungeated."
   */
  planLoadError: string | null;
  /** Re-fetch plan (and thus governance proof) for the current project. */
  refreshProjectPlan: () => Promise<void>;
  /** Open the Save-as-Template modal from any child (e.g. toolbar ... menu). */
  openSaveAsTemplate: () => void;
  /** Open the Duplicate Project modal from any child. */
  openDuplicateProject: () => void;
  /**
   * CLOSED-READONLY-FE-2 — true when project.state is CLOSED.
   * Shared project-state read-only flag for all project write surfaces.
   */
  isReadOnly: boolean;
  /**
   * COLUMN-PERSIST-1: persist shared column-visibility choices (`col:<key>`
   * booleans). Sends only the patch, so two members changing different columns
   * never overwrite each other. Optimistic; rolls back only the keys it changed.
   */
  saveColumnPrefs?: (patch: Record<string, boolean>) => Promise<boolean>;
}

// Context to share project data with child routes
export const ProjectContext = React.createContext<ProjectContextValue>({
  project: null,
  capabilities: null,
  loading: true,
  error: null,
  refresh: async () => {},
  workspaceDisplayName: null,
  overviewSnapshot: null,
  overviewLoading: false,
  refreshOverviewSnapshot: async () => {},
  projectPlan: null,
  hasLiveGovernance: false,
  planLoadError: null,
  refreshProjectPlan: async () => {},
  openSaveAsTemplate: () => {},
  openDuplicateProject: () => {},
  isReadOnly: false,
});

export function useProjectContext() {
  return React.useContext(ProjectContext);
}

export const ProjectPageLayout: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeWorkspaceId, setActiveWorkspace } = useWorkspaceStore();
  const { user } = useAuth();
  const canSaveAsTemplate = isPlatformAdmin(user);

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [capabilities, setCapabilities] = useState<ProjectCapabilities | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspaceDisplayName, setWorkspaceDisplayName] = useState<string | null>(null);
  const [overviewSnapshot, setOverviewSnapshot] = useState<ProjectOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [projectPlan, setProjectPlan] = useState<ProjectPlan | null>(null);
  const [hasLiveGovernance, setHasLiveGovernance] = useState(false);
  const [planLoadError, setPlanLoadError] = useState<string | null>(null);
  const projectWorkspaceRef = useRef<string | null>(null);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [showDuplicateProject, setShowDuplicateProject] = useState(false);
  const [visibleTabIds, setVisibleTabIds] = useState<string[]>([]);
  const [addingViewTabId, setAddingViewTabId] = useState<string | null>(null);

  useEffect(() => {
    if (!project) {
      setVisibleTabIds([]);
      return;
    }
    setVisibleTabIds(readVisibleTabIds(project.columnConfig));
  }, [project?.id, project?.columnConfig]);

  const visibleTabIdSet = useMemo(() => new Set(visibleTabIds), [visibleTabIds]);

  const resourcesTabOn = useFeatureOn('projects.resources');
  const kpisTabOn = useFeatureOn('projects.kpis');
  const calendarTabOn = useFeatureOn('projects.calendar');
  const changeRequestsTabOn = useFeatureOn('projects.change-requests');
  const projectTabOn: Record<string, boolean> = {
    'projects.resources': resourcesTabOn,
    'projects.kpis': kpisTabOn,
    'projects.calendar': calendarTabOn,
    'projects.change-requests': changeRequestsTabOn,
  };

  const visibleTabs = useMemo((): ProjectTab[] => {
    return PROJECT_TABS_ALL.filter(
      (t) => {
        const key = PROJECT_TAB_SWITCH[t.id];
        return visibleTabIdSet.has(t.id) && (!key || projectTabOn[key] === true);
      },
    ).sort(
      (a, b) => tabOrderIndex(a.id) - tabOrderIndex(b.id),
    );
  }, [visibleTabIdSet, resourcesTabOn, kpisTabOn, calendarTabOn, changeRequestsTabOn]);

  const handleAddViewTab = useCallback(
    async (tabId: string) => {
      if (!project || visibleTabIdSet.has(tabId)) return;
      const previousIds = visibleTabIds;
      const previousConfig = project.columnConfig ?? null;
      const nextIds = sortTabIdsByOrder([...visibleTabIds, tabId]);
      const nextConfig = stripLegacyVisibleTabs(
        withVisibleTabs(
          normalizeColumnConfig(project.columnConfig ?? {}),
          nextIds,
        ),
      );

      setVisibleTabIds(nextIds);
      setAddingViewTabId(tabId);
      setProject((p) => (p ? { ...p, columnConfig: nextConfig } : p));

      try {
        const saved = await projectsApi.updateColumnConfig(project.id, nextConfig);
        setProject((p) => (p ? { ...p, columnConfig: saved } : p));
        setVisibleTabIds(readVisibleTabIds(saved));
      } catch {
        setVisibleTabIds(previousIds);
        setProject((p) => (p ? { ...p, columnConfig: previousConfig } : p));
        toast.error('Could not save view. Please try again.');
      } finally {
        setAddingViewTabId(null);
      }
    },
    [project, visibleTabIdSet, visibleTabIds],
  );

  const saveColumnPrefs = useCallback(
    async (patch: Record<string, boolean>): Promise<boolean> => {
      if (!project) return false;
      const prevColumns = normalizeColumnConfig(project.columnConfig ?? {}).columns;
      const patchKeys = Object.keys(patch);
      const applyPatch = (p: ProjectDetail | null) =>
        p
          ? {
              ...p,
              columnConfig: {
                ...normalizeColumnConfig(p.columnConfig ?? {}),
                columns: {
                  ...normalizeColumnConfig(p.columnConfig ?? {}).columns,
                  ...patch,
                },
              },
            }
          : p;
      setProject(applyPatch);
      try {
        const saved = await projectsApi.updateColumnConfig(project.id, { columns: patch });
        setProject((p) => (p ? { ...p, columnConfig: saved } : p));
        return true;
      } catch (e: any) {
        const status = e?.response?.status ?? e?.status;
        setProject((p) => {
          if (!p) return p;
          const cfg = normalizeColumnConfig(p.columnConfig ?? {});
          const columns = { ...cfg.columns };
          for (const k of patchKeys) {
            if (k in prevColumns) columns[k] = prevColumns[k];
            else delete columns[k];
          }
          return { ...p, columnConfig: { ...cfg, columns } };
        });
        toast.error(
          status === 403
            ? 'You need to be a member of this workspace to change the columns.'
            : 'Could not save the column change. Please try again.',
        );
        return false;
      }
    },
    [project],
  );

  // Deep-link: ?action=save-as-template or ?action=duplicate opens the modal on arrival.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const action = sp.get('action');
    // Only this layout's own actions are consumed here; a tab's action (import, custom-fields,
    // new-task, new-phase) is left in the URL for that tab to read.
    if (action !== 'save-as-template' && action !== 'duplicate') return;
    if (action === 'save-as-template' && canSaveAsTemplate) setShowSaveAsTemplate(true);
    if (action === 'duplicate') setShowDuplicateProject(true);
    sp.delete('action');
    const next = sp.toString();
    navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate, canSaveAsTemplate]);

  const fetchOverviewForShell = useCallback(async (pid: string, wsid: string) => {
    setOverviewLoading(true);
    try {
      const response = await api.get(`/work/projects/${pid}/overview`, {
        headers: { 'x-workspace-id': wsid },
      });
      const data = unwrapApiData(response);
      setOverviewSnapshot(normalizeProjectOverview(data));
    } catch {
      setOverviewSnapshot(null);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  /** OV-1 A3/A6 — plan gates are earned proof; plan failure must not look like "no gates". */
  const fetchPlanForShell = useCallback(async (pid: string, wsid: string) => {
    try {
      const response = await api.get(`/work/projects/${pid}/plan`, {
        headers: { 'x-workspace-id': wsid },
      });
      // Interceptor may return { __zephixInner, __zephixMeta } when envelope has meta —
      // must unwrap or phases look empty and Governed never earns (silent falsehood).
      const data = unwrapApiData(response);
      const plan = mapProjectPlanFromApi(data);
      setProjectPlan(plan);
      setHasLiveGovernance(projectHasActiveGateDefinitions(plan));
      setPlanLoadError(null);
    } catch {
      // Cannot prove live gates — do NOT claim "ungeated"; surface the failure.
      setProjectPlan(null);
      setHasLiveGovernance(false);
      setPlanLoadError('Failed to verify project governance. Plan could not be loaded.');
    }
  }, []);

  const refreshOverviewSnapshot = useCallback(async () => {
    const ws = projectWorkspaceRef.current;
    if (!projectId || !ws) return;
    await fetchOverviewForShell(projectId, ws);
  }, [projectId, fetchOverviewForShell]);

  const refreshProjectPlan = useCallback(async () => {
    const ws = projectWorkspaceRef.current;
    if (!projectId || !ws) return;
    await fetchPlanForShell(projectId, ws);
  }, [projectId, fetchPlanForShell]);

  // Determine active tab from current path
  const getActiveTab = (): TabId => {
    const path = location.pathname;
    if (path.includes('/change-requests')) return 'change-requests';
    if (path.includes('/budget')) return 'budget';
    if (path.includes('/kpis')) return 'kpis';
    if (path.includes('/plan')) return 'plan';
    if (path.includes('/tasks')) return 'tasks';
    if (path.includes('/board')) return 'board';
    if (path.includes('/table')) return 'table';
    if (path.includes('/gantt')) return 'gantt';
    if (path.includes('/calendar')) return 'calendar';
    if (path.includes('/documents') || path.includes('/tools/docs') || path.includes('/tools/workflow')) {
      return 'documents';
    }
    if (path.includes('/governance')) return 'governance';
    if (path.includes('/risks')) return 'risks';
    if (path.includes('/resources')) return 'resources';
    return 'overview';
  };

  const activeTab = getActiveTab();

  // Load project data
  const loadProject = async () => {
    if (!projectId) {
      setError('No project ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setWorkspaceDisplayName(null);
      setOverviewSnapshot(null);
      setCapabilities(null);
      setProjectPlan(null);
      setHasLiveGovernance(false);
      setPlanLoadError(null);
      projectWorkspaceRef.current = null;
      const projectData = await projectsApi.getProject(projectId);
      if (projectData) {
        setProject(projectData);
        if (projectData.workspaceId) {
          projectWorkspaceRef.current = projectData.workspaceId;
          setActiveWorkspace(projectData.workspaceId);
          try {
            const ws = await getWorkspace(projectData.workspaceId);
            setWorkspaceDisplayName(ws.name);
          } catch {
            setWorkspaceDisplayName(null);
          }
          try {
            const caps = await getProjectCapabilities(projectData.workspaceId, projectId);
            setCapabilities(caps);
          } catch {
            setCapabilities(null);
          }
          await Promise.all([
            fetchOverviewForShell(projectId, projectData.workspaceId),
            fetchPlanForShell(projectId, projectData.workspaceId),
          ]);
        } else {
          projectWorkspaceRef.current = null;
          setOverviewSnapshot(null);
          setProjectPlan(null);
          setHasLiveGovernance(false);
          setPlanLoadError(null);
        }
      } else {
        setError('Project not found');
      }
    } catch (err: any) {
      console.error('Failed to load project:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
    // Intentionally projectId only: loadProject syncs active workspace to the
    // project's workspaceId; depending on activeWorkspaceId would double-fetch.
  }, [projectId]);

  // Waterfall redirect removed — all projects land on Overview first.

  // Handle tab navigation
  const handleTabClick = (tab: ProjectTab) => {
    const basePath = `/projects/${projectId}`;
    const nextPath = `${basePath}${tab.path}`;
    const qs = location.search ?? '';
    navigate({ pathname: nextPath, search: qs });
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          title="Unable to load project"
          description={error}
          icon={<AlertTriangle className="h-12 w-12" />}
          action={
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/projects')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
              >
                Back to Projects
              </button>
              <button
                onClick={loadProject}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Try Again
              </button>
            </div>
          }
        />
      </div>
    );
  }

  // No project found
  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          title="Project not found"
          description="The project you're looking for doesn't exist or you don't have access to it."
          icon={<Folder className="h-12 w-12" />}
          action={
            <button
              onClick={() => navigate('/projects')}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              Back to Projects
            </button>
          }
        />
      </div>
    );
  }

  const duplicateWorkspaceId = project.workspaceId ?? activeWorkspaceId ?? '';

  const showWorkSurfaceToolbar =
    activeTab === 'tasks' || activeTab === 'board' || activeTab === 'gantt';

  return (
    <ProjectContext.Provider
      value={{
        project,
        capabilities,
        loading,
        error,
        refresh: loadProject,
        workspaceDisplayName,
        overviewSnapshot,
        overviewLoading,
        refreshOverviewSnapshot,
        projectPlan,
        hasLiveGovernance,
        planLoadError,
        refreshProjectPlan,
        openSaveAsTemplate: () => {
          if (canSaveAsTemplate) setShowSaveAsTemplate(true);
        },
        openDuplicateProject: () => setShowDuplicateProject(true),
        isReadOnly: isProjectReadOnly(project?.state),
        saveColumnPrefs,
      }}
    >
      <WorkSurfaceUiProvider>
      <div className="min-h-full bg-slate-50">
        <div className="bg-white border-b border-slate-200">
          <div className="container mx-auto px-4 py-4">
            {/* Persistent project header — visible on all tabs, editable */}
            <EditableProjectHeader
              project={project}
              onSave={async (patch) => {
                await projectsApi.updateProjectSettings(project.id, patch);
                await loadProject();
              }}
              onSaveAsTemplate={() => {
                if (canSaveAsTemplate) setShowSaveAsTemplate(true);
              }}
              onDuplicateProject={() => setShowDuplicateProject(true)}
            />

            {project && (
              <>
                {canSaveAsTemplate ? (
                  <SaveAsTemplateModal
                    open={showSaveAsTemplate}
                    onClose={() => setShowSaveAsTemplate(false)}
                    projectId={project.id}
                    projectName={project.name}
                  />
                ) : null}
                {duplicateWorkspaceId ? (
                  <DuplicateProjectModal
                    open={showDuplicateProject}
                    onClose={() => setShowDuplicateProject(false)}
                    projectId={project.id}
                    projectName={project.name}
                    workspaceId={duplicateWorkspaceId}
                  />
                ) : null}
              </>
            )}

            {/* Tab Navigation */}
            <div className="mt-6 -mb-px">
              <nav className="flex flex-wrap items-end gap-6" aria-label="Project sections">
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabClick(tab)}
                      title={`Open ${tab.label}`}
                      className={`
                        flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors
                        ${isActive
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                        }
                      `}
                    >
                      <Icon className="h-4 w-4" aria-hidden />
                      {tab.label}
                    </button>
                  );
                })}
                {!isProjectReadOnly(project?.state) ? (
                  <ProjectAddViewPopover
                    visibleTabIds={visibleTabIdSet}
                    onAddViewTab={(tabId) => void handleAddViewTab(tabId)}
                    addingTabId={addingViewTabId}
                    tabSwitchOn={projectTabOn}
                  />
                ) : null}
              </nav>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="container mx-auto px-4 py-6">
          {showWorkSurfaceToolbar && <ProjectWorkToolbar />}
          <Outlet />
        </div>
      </div>
      </WorkSurfaceUiProvider>
    </ProjectContext.Provider>
  );
};

/* ── Editable Project Header ─────────────────────────────────── */

export function EditableProjectHeader({
  project,
  onSave,
  onSaveAsTemplate,
  onDuplicateProject,
}: {
  project: ProjectDetail;
  onSave: (patch: { name?: string; description?: string }) => Promise<void>;
  onSaveAsTemplate: () => void;
  onDuplicateProject: () => void;
}) {
  // Live workspace role (GET /workspaces/:id/role). can('project.edit') reads
  // the Zustand workspaceRole, which has no production setter, so a MEMBER
  // who can PATCH the project never reached the editor. canEdit is true for
  // OWNER, ADMIN, and MEMBER. createdById is not consulted.
  const { canEdit } = useProjectPermissions(project);
  const closed = isProjectReadOnly(project.state);
  const allowProjectEdit = canEdit && !closed;
  const { planLoadError, refreshProjectPlan } = useProjectContext();

  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [nameVal, setNameVal] = useState(project.name);
  const [descVal, setDescVal] = useState(project.description || '');
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);

  // Sync if project changes externally
  useEffect(() => { setNameVal(project.name); }, [project.name]);
  useEffect(() => { setDescVal(project.description || ''); }, [project.description]);

  const saveName = async () => {
    const trimmed = nameVal.trim();
    if (!trimmed || trimmed === project.name) { setEditingName(false); setNameVal(project.name); return; }
    setSaving(true);
    try { await onSave({ name: trimmed }); } catch { setNameVal(project.name); }
    finally { setSaving(false); setEditingName(false); }
  };

  const saveDesc = async () => {
    const trimmed = descVal.trim();
    if (trimmed === (project.description || '')) { setEditingDesc(false); return; }
    setSaving(true);
    try { await onSave({ description: trimmed || undefined }); } catch { setDescVal(project.description || ''); }
    finally { setSaving(false); setEditingDesc(false); }
  };

  return (
    <div className="relative mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white p-6">
      {closed ? (
        <p className="mb-3 text-sm font-medium text-slate-700" data-testid="project-closed-banner">
          Closed — read-only. Reopen to make changes.
        </p>
      ) : null}
      <ProjectLifecycleControls project={project} />
      <div className="relative">
        <ProjectHeaderActionsMenu
          project={project}
          onSaveAsTemplate={onSaveAsTemplate}
          onDuplicateProject={onDuplicateProject}
        />
        {/* Editable project name */}
        {editingName && allowProjectEdit ? (
          <input
            ref={nameRef}
            autoFocus
            value={nameVal}
            onChange={(e) => setNameVal(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') { setNameVal(project.name); setEditingName(false); } }}
            disabled={saving}
            className="w-full rounded-lg bg-slate-50 px-2 py-1 text-2xl font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-300"
          />
        ) : (
          <div className="flex min-w-0 flex-wrap items-center gap-2 pr-10">
            <h1
              className={`w-full min-w-0 whitespace-normal break-normal rounded-lg px-2 py-1 -mx-2 text-2xl font-bold leading-8 text-slate-900 sm:max-w-[min(100%,42rem)] ${allowProjectEdit ? 'cursor-text transition-colors hover:bg-slate-50' : 'cursor-default'}`}
              onClick={() => {
                if (allowProjectEdit) setEditingName(true);
              }}
              title={allowProjectEdit ? 'Click to edit project name' : project.name}
            >
              {project.name}
            </h1>
            {planLoadError ? (
              <span
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                title={planLoadError}
                role="alert"
                data-testid="project-governance-unverified"
              >
                <AlertTriangle className="h-3 w-3 text-amber-600" aria-hidden />
                Governance unverified
                <button
                  type="button"
                  onClick={() => void refreshProjectPlan()}
                  className="ml-0.5 underline decoration-amber-700/60 hover:decoration-amber-900"
                >
                  Retry
                </button>
              </span>
            ) : null}
          </div>
        )}

        {/* Editable description */}
        {editingDesc && allowProjectEdit ? (
          <textarea
            ref={descRef}
            autoFocus
            aria-label="Project description"
            value={descVal}
            onChange={(e) => setDescVal(e.target.value)}
            onBlur={() => {
              window.setTimeout(() => {
                void saveDesc();
              }, 0);
            }}
            onKeyDown={(e) => { if (e.key === 'Escape') { setDescVal(project.description || ''); setEditingDesc(false); } }}
            disabled={saving}
            rows={3}
            className="mt-2 w-full resize-none rounded-lg bg-slate-50 px-2 py-1.5 text-sm leading-relaxed text-slate-600 outline-none focus:ring-2 focus:ring-blue-300"
            placeholder="Add a project description..."
          />
        ) : allowProjectEdit ? (
          <button
            type="button"
            className={`mt-2 w-full rounded-lg px-2 py-1 -mx-2 text-left text-sm leading-relaxed ${
              project.description?.trim()
                ? 'text-slate-600'
                : 'italic text-slate-400'
            } transition-colors hover:bg-slate-50`}
            onClick={() => setEditingDesc(true)}
          >
            {project.description?.trim() || 'Add a project description...'}
          </button>
        ) : (
          <p
            className={`mt-2 rounded-lg px-2 py-1 -mx-2 text-sm leading-relaxed ${
              project.description?.trim()
                ? 'text-slate-600'
                : 'italic text-slate-400'
            } cursor-default`}
          >
            {project.description?.trim() || 'Add a project description...'}
          </p>
        )}
      </div>
    </div>
  );
}

export default ProjectPageLayout;
